import {
  IncidentTableData,
  IncidentFeatureCollection,
  IncidentFeature,
  HoveredFeature,
  IncidentType,
  ApiRef,
  IncidentFeaturePropertiesWithApiRef,
} from "../types";
import { MapMouseEvent, MapRef } from "react-map-gl/mapbox";
import { bbox, featureCollection } from "@turf/turf";
import { Feature, FeatureCollection } from "geojson";
import { incidentMarkerLayerId, styleMapper } from "./styles";

export const createSelectedIncidentCollection = (
  selectedIncidents: Array<IncidentTableData>,
): IncidentFeatureCollection | null => {
  const features = selectedIncidents.flatMap((selectedIncident) =>
    selectedIncident.incidentFileShapes.features.map((feature) => ({
      ...feature,
      properties: {
        ...feature.properties,
        apiRef: selectedIncident.apiRef,
      },
    })),
  ) as Array<IncidentFeature>;

  if (!features.length) return null;

  return featureCollection(features);
};

const getIncidentFeatureIndex = (value: unknown): number | null => {
  if (typeof value === "number") return value;
  if (typeof value !== "string") return null;

  const parsedValue = Number(value);

  return Number.isInteger(parsedValue) ? parsedValue : null;
};

export const getIncidentFeatureFromMarker = (
  event: MapMouseEvent,
  collection: IncidentFeatureCollection | null,
): IncidentFeature | null => {
  if (!collection) return null;

  const markerFeature = event.features?.find(
    (feature) => feature.layer?.id === incidentMarkerLayerId,
  );
  const interactiveFeature = markerFeature ?? event.features?.[0];
  const incidentFeatureIndex = getIncidentFeatureIndex(
    interactiveFeature?.properties?.incidentFeatureIndex,
  );
  const incidentId = interactiveFeature?.properties?.incidentId;
  const polygonLabel = interactiveFeature?.properties?.polygonLabel;

  if (incidentFeatureIndex !== null)
    return collection.features[incidentFeatureIndex] ?? null;

  if (typeof incidentId !== "string") return null;

  return (
    collection.features.find((feature) => {
      const hasMatchingIncidentId =
        feature.properties.incidentId === incidentId;

      if (typeof polygonLabel !== "string") return hasMatchingIncidentId;

      return (
        hasMatchingIncidentId &&
        "polygonLabel" in feature.properties &&
        feature.properties.polygonLabel === polygonLabel
      );
    }) ?? null
  );
};

export const createHoveredIncidentFeature = (
  feature: IncidentFeature,
): HoveredFeature => {
  const properties = feature.properties as IncidentFeaturePropertiesWithApiRef;

  return {
    properties,
    geometry: feature.geometry,
    type: properties.incident,
    apiRef: properties.apiRef ?? null,
  };
};

export const fitFeatureBounds = (
  map: MapRef | null,
  feature: IncidentFeature,
) => {
  if (!map) return;

  const featureBounds = bbox(feature);

  map.fitBounds(
    [
      [featureBounds[0], featureBounds[1]],
      [featureBounds[2], featureBounds[3]],
    ],
    { padding: 40, duration: 1000 },
  );
};

export const getHoveredFeature = (event: MapMouseEvent): HoveredFeature => {
  const feature = event.features?.[0];

  if (!feature?.layer?.id)
    return { properties: null, geometry: null, type: null, apiRef: null };
  if (feature.layer.id === `${styleMapper.buffer.id}-fill`) {
    return {
      properties: feature.properties as Record<string, unknown>,
      geometry: feature.geometry,
      type: "buffer",
      apiRef: null,
    };
  }
  if (feature.layer.id === `${styleMapper.outline.id}-fill`) {
    return {
      properties: feature.properties as Record<string, unknown>,
      geometry: feature.geometry,
      type: "outline",
      apiRef: null,
    };
  }

  return {
    properties: feature.properties as Record<string, unknown>,
    geometry: feature.geometry,
    type: feature.properties?.incident as IncidentType,
    apiRef: feature.properties?.apiRef as ApiRef,
  };
};

export const isSameFeature = (
  firstFeature: HoveredFeature,
  secondFeature: HoveredFeature,
) =>
  JSON.stringify(firstFeature.properties) ===
    JSON.stringify(secondFeature.properties) &&
  firstFeature.type === secondFeature.type;

export const createInteractiveFeatureCollection = (
  feature: HoveredFeature,
): FeatureCollection | null => {
  if (
    !feature.properties ||
    !feature.geometry ||
    !feature.type ||
    feature.type === "buffer" ||
    feature.type === "outline"
  )
    return null;

  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        properties: feature.properties,
        geometry: feature.geometry,
      } as Feature,
    ],
  };
};
